// The invitee's side (37-desyr-id-invitations.md): the open invitations in "Your circles" and in the
// switcher's list (Join, Decline, an email not confirmed, an admin invitation without a passkey),
// and the passkey card for owners and admins.
import { useEffect, useState } from 'react';
import { Spinner } from '../blocks/Wait';
import { Avatar, NoticeCard } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Confirm, Dialog } from '../blocks/Dialog';
import { readSession } from '../core/session';
import { toast } from '../blocks/Toast';
import type { Config, DesyrIdConfig } from '../core/config';
import { Said } from '../core/errors';
import { t } from '../i18n';
import { idCall, type Circle, type Invitation } from './api';
import { pressed } from './pressed';
import { loadCircles, signInCircles, syncCircles, useCircles } from './circles';
import { switchTo } from '../accounts/store';
import { idWords, usePasskeyCard } from './manage';
import { keepAfter, keepReturn, readId, stopLeaving } from './store';

/** A link to the desyr ID's own page, in this window, back to the page the person is on (36, Behaviour 3). */
const IdPage = ({ id, children }: { id: DesyrIdConfig; children: string }) => (
  <a className="btn btn-soft btn-small" href={id.account} onClick={() => keepReturn(location.pathname + location.search)}>
    {children}
  </a>
);

const ConfirmEmail = ({ id }: { id: DesyrIdConfig }) => (
  <p className="settings-note" role="status">
    {t('id.confirmEmail')} <IdPage id={id}>{t('id.manage')}</IdPage>
  </p>
);

/** The open invitations (only while there are any); `inSwitcher`: without the list's own heading. */
export function Invitations({ config, inSwitcher }: { config: Config; inSwitcher?: boolean }) {
  const { answer } = useCircles();
  const id = config.desyrId!;
  if (answer && !answer.emailVerified) return <ConfirmEmail id={id} />;
  const open = answer?.invitations ?? [];
  if (!open.length) return null;
  return (
    <>
      {!inSwitcher && <h3>{t('id.invitations')}</h3>}
      <ul className="id-circles" aria-label={t('id.invitations')}>
        {open.map((i) => (
          <InvitationRow key={i.id} config={config} invitation={i} />
        ))}
      </ul>
    </>
  );
}

function InvitationRow({ config, invitation: i }: { config: Config; invitation: Invitation }) {
  const id = config.desyrId!;
  const { logos } = useCircles();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<'passkey' | 'email' | string>();
  const [declining, setDeclining] = useState(false);
  const circle = i.circle.name;
  // Join and Decline are presses that need desyr ID (36, Behaviour 1.4): its sign-in once when its session is over.
  const call = (what: 'accept' | 'decline') => pressed(() => idCall<{ circle: Circle }>(id, `/invitations/${encodeURIComponent(i.id)}/${what}`, { method: 'POST' }));
  /** A refusal: one no longer open leaves the list with a toast; the others show under it. */
  const refused = (e: unknown) => {
    const code = (e as { code?: string }).code;
    if (code === 'desyr.invite.not_found') return void (toast(t('id.notOpen')), loadCircles(id));
    setProblem(code === 'desyr.id.passkey_needed' ? 'passkey' : code === 'desyr.id.email_unverified' ? 'email' : idWords(e, { email: readId()?.email, name: i.name, circle }));
  };
  const join = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      const joined = (await call('accept')).circle;
      stopLeaving(joined.server); // left earlier, joined again (39)
      // The sessions call brings the joined circle without leaving the page (37, Behaviour 1, rule 2;
      // v1.58.15): with a circle open, "Joined <Circle>" and Open; in "Your circles", its row "Ready".
      const outcome = await syncCircles(config);
      const given = 'answer' in outcome ? outcome.answer.circles.find((c) => c.server === joined.server)?.session?.state : undefined;
      if (given && given !== 'hop') return void (readSession() && given === 'ready' && toast(t('id.joined', { circle }), { label: t('id.openCircle'), run: () => switchTo(joined.mxid, '/', circle) }));
      await loadCircles(id);
      // Only when the call answers `hop` for it, or isn't there: a hop to the joined circle; with a circle
      // open, the person comes back to this page and stays in it, with "Joined <Circle>" and Open (v1.58.9).
      if (readSession()) (keepReturn(location.pathname + location.search), keepAfter({ do: 'joined', name: circle, mxid: joined.mxid }));
      void signInCircles(config, joined.server);
    } catch (e) {
      refused(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-desyr-invitation={i.id}>
      <Avatar id={i.circle.slug} name={circle} src={logos[i.circle.slug]} size={40} round={false} letters={2} />
      <strong>{circle}</strong>
      <span>{t('id.invitedYou', { inviter: i.invitedBy, name: i.name })}</span>
      <Button variant="filled" small busy={busy} onClick={() => void join()}>
        {busy ? t('id.joining') : t('id.join', { circle })}
      </Button>
      <Button variant="text" small disabled={busy} onClick={() => setDeclining(true)}>
        {t('id.decline')}
      </Button>
      <span role="status" className="settings-note">
        {busy && <span className="visually-hidden">{t('id.joining')}</span>}
        {problem === 'passkey' ? (
          <>
            {t('id.circleNeedsPasskey', { circle })} <IdPage id={id}>{t('id.addPasskey')}</IdPage>
          </>
        ) : problem === 'email' ? (
          <>
            {t('id.confirmEmail')} <IdPage id={id}>{t('id.manage')}</IdPage>
          </>
        ) : (
          problem
        )}
      </span>
      {declining && (
        <Confirm
          narrow
          title={t('id.declineTitle', { circle })}
          text={null}
          action={t('id.decline')}
          onClose={() => setDeclining(false)}
          onConfirm={() =>
            call('decline').then(
              () => loadCircles(id),
              (e) => {
                if ((e as { code?: string }).code === 'desyr.invite.not_found') return refused(e);
                throw new Said(idWords(e, { name: i.name, circle }));
              },
            )
          }
        />
      )}
    </li>
  );
}

/** The switcher's "Invitations" list (37): asked again as it opens; it closes with the last invitation. */
export function InvitationsDialog({ config, onClose }: { config: Config; onClose: () => void }) {
  const { answer } = useCircles();
  // The rows only from the fresh answer: an older one may still hold an invitation revoked meanwhile.
  const [fresh, setFresh] = useState(false);
  useEffect(() => void loadCircles(config.desyrId!).then(() => setFresh(true)), []);
  useEffect(() => {
    if (fresh && answer && !answer.invitations?.length) onClose();
  }, [answer, fresh]);
  return (
    <Dialog title={t('id.invitations')} onClose={onClose}>
      {fresh ? <Invitations config={config} inSwitcher /> : <Spinner />}
    </Dialog>
  );
}

/** The passkey card (37, Passkey prompts): for an owner or admin without a passkey, at most once a day. */
export function PasskeyCard({ id }: { id: DesyrIdConfig }) {
  const circle = usePasskeyCard();
  return circle ? <PasskeyNote id={id} line={t('id.youManage', { circle: circle.name })} /> : null;
}

/** The card itself; also the answer to a passkey sign-in that still had none (Behaviour 3.1). */
export const PasskeyNote = ({ id, line }: { id: DesyrIdConfig; line: string }) => (
  <div role="note" aria-label={t('id.addPasskey')} data-desyr-passkey-card="">
    <NoticeCard title={t('id.addPasskey')} description={line} after={<IdPage id={id}>{t('id.addPasskey')}</IdPage>} />
  </div>
);
